import React from 'react';
import { FiSearch, FiAlertCircle } from 'react-icons/fi';
import { useAgentKnowledgeGaps } from '../../hooks/useAgentAnalytics';
import type { AnalyticsDateRange, AnalyticsPeriod } from '../../services/agentAnalyticsApi';

interface KnowledgeGapsCardProps {
  agentId: string;
  period: AnalyticsPeriod;
  /** Intervalo personalizado — substitui a janela do `period` */
  range?: AnalyticsDateRange;
}

function severityColor(severity: 'low' | 'medium' | 'high'): string {
  if (severity === 'high') return '#ef4444';
  if (severity === 'medium') return '#f59e0b';
  return '#6b7280';
}

export const KnowledgeGapsCard: React.FC<KnowledgeGapsCardProps> = ({
  agentId,
  period,
  range,
}) => {
  const { data: gaps, isLoading } = useAgentKnowledgeGaps(agentId, period, false, range);

  return (
    <div
      className="rounded-lg border p-4"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3
          className="text-sm font-semibold"
          style={{ color: 'var(--theme-card-text)' }}
        >
          Lacunas de conhecimento
        </h3>
        <FiSearch
          className="h-5 w-5"
          style={{ color: 'var(--theme-primary)' }}
        />
      </div>

      {isLoading && (
        <div
          className="text-sm"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          Carregando...
        </div>
      )}

      {!isLoading && gaps && gaps.patterns.length === 0 && (
        <div
          className="text-sm flex items-start gap-2"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          <FiAlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>Nenhuma lacuna detectada no período.</span>
        </div>
      )}

      {!isLoading && gaps && gaps.patterns.length > 0 && (
        <>
          <div
            className="text-3xl font-bold mb-1"
            style={{ color: 'var(--theme-card-text)' }}
          >
            {gaps.totalOccurrences}
          </div>
          <div
            className="text-xs mb-3"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            ocorrências em {gaps.patterns.length} padrões
          </div>

          <div className="space-y-2">
            {gaps.patterns.slice(0, 5).map((pattern) => (
              <div
                key={pattern.pattern}
                className="flex items-center justify-between text-sm"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div
                    className="h-2 w-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: severityColor(pattern.severity) }}
                  />
                  <span
                    className="truncate"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {pattern.label}
                  </span>
                </div>
                <span
                  className="text-xs font-medium ml-2"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  {pattern.count}×
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
